/**
 * Purchasing route factory. CLAUDE-A spreads these under the authed route:
 *
 *   children: [...purchasingRoutes(authedRoute)]
 *
 * Pages stay router-free (props in, callbacks out); this file is the only
 * place purchasing code touches TanStack Router, and the only place it reads
 * the session. Every control a page renders is gated here by an exact
 * `hasPermission` check against the route-policy permission strings, so a
 * user without a grant never sees a button that would 403.
 */
import {
  createRoute,
  useNavigate,
  useParams,
  useSearch,
  type AnyRoute,
} from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";

import { PoListPage } from "./pages/po-list-page";
import { PoEditorPage } from "./pages/po-editor-page";
import { PoReceivePage } from "./pages/po-receive-page";
import { GrnListPage } from "./pages/grn-list-page";
import { GrnDetailPage } from "./pages/grn-detail-page";
import { LandedCostListPage } from "./pages/landed-cost-list-page";
import { LandedCostEditorPage } from "./pages/landed-cost-editor-page";
import { ReorderSuggestionsPage } from "./pages/reorder-suggestions-page";
import { SupplierItemsPage } from "./pages/supplier-items-page";
import { PurchasingConfigPage } from "./pages/purchasing-config-page";
import { PURCHASING_PERMISSIONS as P, PurchasingPermissionBoundary } from "./permissions";
import type { PurchaseOrderStatus } from "./api";

export interface PoListSearch {
  /** Deep link from partner 360 / a converted suggestion. */
  supplier_id?: string;
  status?: PurchaseOrderStatus;
}

export interface GrnListSearch {
  purchase_order_id?: string;
}

/** Absent keys are OMITTED (never explicit undefined) per router.tsx house rules. */
function pickString(source: Record<string, unknown>, key: string): { [k: string]: string } {
  const value = source[key];
  return typeof value === "string" && value !== "" ? { [key]: value } : {};
}

function usePurchasingNav() {
  const navigate = useNavigate();
  return {
    openPurchaseOrder: (purchaseOrderId: string) =>
      void navigate({
        to: "/purchasing/purchase-orders/$purchaseOrderId",
        params: { purchaseOrderId },
      } as never),
    openPurchaseOrders: (supplierId?: string) =>
      void navigate({
        to: "/purchasing/purchase-orders",
        search: supplierId === undefined ? {} : { supplier_id: supplierId },
      } as never),
    newPurchaseOrder: () => void navigate({ to: "/purchasing/purchase-orders/new" } as never),
    openReceive: (purchaseOrderId: string) =>
      void navigate({
        to: "/purchasing/purchase-orders/$purchaseOrderId/receive",
        params: { purchaseOrderId },
      } as never),
    openGrns: (purchaseOrderId?: string) =>
      void navigate({
        to: "/purchasing/grns",
        search: purchaseOrderId === undefined ? {} : { purchase_order_id: purchaseOrderId },
      } as never),
    openGrn: (grnId: string) =>
      void navigate({ to: "/purchasing/grns/$grnId", params: { grnId } } as never),
    openLandedCosts: () => void navigate({ to: "/purchasing/landed-costs" } as never),
    newLandedCost: () => void navigate({ to: "/purchasing/landed-costs/new" } as never),
    openLandedCost: (landedCostId: string) =>
      void navigate({
        to: "/purchasing/landed-costs/$landedCostId",
        params: { landedCostId },
      } as never),
  };
}

function PoListRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as PoListSearch;
  return (
    <PurchasingPermissionBoundary permission={P.purchaseOrderRead}>
      <PoListPage
        {...(search.supplier_id === undefined ? {} : { initialSupplierId: search.supplier_id })}
        {...(search.status === undefined ? {} : { initialStatus: search.status })}
        canCreate={hasPermission(P.purchaseOrderCreate)}
        canReadSuppliers={hasPermission(P.partyRead)}
        onOpenPurchaseOrder={nav.openPurchaseOrder}
        onCreatePurchaseOrder={nav.newPurchaseOrder}
      />
    </PurchasingPermissionBoundary>
  );
}

function PoNewRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  return (
    <PurchasingPermissionBoundary permission={P.purchaseOrderCreate}>
      <PoEditorPage
        canCreate
        canReadSuppliers={hasPermission(P.partyRead)}
        canReadVariants={hasPermission(P.variantRead)}
        canReadUoms={hasPermission(P.uomRead)}
        canReadConfig={hasPermission(P.configRead)}
        onCreated={nav.openPurchaseOrder}
        onDeleted={() => nav.openPurchaseOrders()}
        onOpenReceive={nav.openReceive}
        onOpenGrns={nav.openGrns}
      />
    </PurchasingPermissionBoundary>
  );
}

function PoEditorRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { purchaseOrderId?: string };
  return (
    <PurchasingPermissionBoundary permission={P.purchaseOrderRead}>
      <PoEditorPage
        key={params.purchaseOrderId}
        {...(params.purchaseOrderId === undefined
          ? {}
          : { purchaseOrderId: params.purchaseOrderId })}
        canUpdate={hasPermission(P.purchaseOrderUpdate)}
        canDelete={hasPermission(P.purchaseOrderDelete)}
        canSubmit={hasPermission(P.purchaseOrderSubmit)}
        canSend={hasPermission(P.purchaseOrderSend)}
        canClose={hasPermission(P.purchaseOrderClose)}
        canReceive={hasPermission(P.grnPost)}
        canReadTransmissions={hasPermission(P.transmissionRead)}
        canReadSuppliers={hasPermission(P.partyRead)}
        canReadVariants={hasPermission(P.variantRead)}
        canReadUoms={hasPermission(P.uomRead)}
        canReadConfig={hasPermission(P.configRead)}
        onCreated={nav.openPurchaseOrder}
        onDeleted={() => nav.openPurchaseOrders()}
        onOpenReceive={nav.openReceive}
        onOpenGrns={nav.openGrns}
      />
    </PurchasingPermissionBoundary>
  );
}

function PoReceiveRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { purchaseOrderId?: string };
  return (
    <PurchasingPermissionBoundary permission={P.grnPost}>
      <PoReceivePage
        key={params.purchaseOrderId}
        purchaseOrderId={params.purchaseOrderId ?? ""}
        canPost={hasPermission(P.grnPost)}
        canReadVariants={hasPermission(P.variantRead)}
        canReadBarcodes={hasPermission(P.barcodeRead)}
        canReadWarehouses={hasPermission(P.warehouseRead)}
        canReadBatches={hasPermission(P.batchRead)}
        canReadConfig={hasPermission(P.configRead)}
        onPosted={nav.openGrn}
        onBack={() => nav.openPurchaseOrder(params.purchaseOrderId ?? "")}
      />
    </PurchasingPermissionBoundary>
  );
}

function GrnListRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as GrnListSearch;
  return (
    <PurchasingPermissionBoundary permission={P.grnRead}>
      <GrnListPage
        {...(search.purchase_order_id === undefined
          ? {}
          : { initialPurchaseOrderId: search.purchase_order_id })}
        canReadSuppliers={hasPermission(P.partyRead)}
        canReadWarehouses={hasPermission(P.warehouseRead)}
        onOpenGrn={nav.openGrn}
        {...(hasPermission(P.purchaseOrderRead)
          ? { onOpenPurchaseOrder: nav.openPurchaseOrder }
          : {})}
      />
    </PurchasingPermissionBoundary>
  );
}

function GrnDetailRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { grnId?: string };
  return (
    <PurchasingPermissionBoundary permission={P.grnRead}>
      <GrnDetailPage
        key={params.grnId}
        grnId={params.grnId ?? ""}
        canReadVariants={hasPermission(P.variantRead)}
        canReadWarehouses={hasPermission(P.warehouseRead)}
        canReadPurchaseOrders={hasPermission(P.purchaseOrderRead)}
        onOpenPurchaseOrder={nav.openPurchaseOrder}
        onBack={() => nav.openGrns()}
      />
    </PurchasingPermissionBoundary>
  );
}

function LandedCostListRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  return (
    <PurchasingPermissionBoundary permission={P.landedCostRead}>
      <LandedCostListPage
        canCreate={hasPermission(P.landedCostCreate)}
        canReadSuppliers={hasPermission(P.partyRead)}
        onOpenLandedCost={nav.openLandedCost}
        onCreateLandedCost={nav.newLandedCost}
      />
    </PurchasingPermissionBoundary>
  );
}

function LandedCostNewRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  return (
    <PurchasingPermissionBoundary permission={P.landedCostCreate}>
      <LandedCostEditorPage
        canCreate
        canReadSuppliers={hasPermission(P.partyRead)}
        canReadVariants={hasPermission(P.variantRead)}
        canReadGrns={hasPermission(P.grnRead)}
        onCreated={nav.openLandedCost}
        onDeleted={nav.openLandedCosts}
      />
    </PurchasingPermissionBoundary>
  );
}

function LandedCostDetailRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { landedCostId?: string };
  return (
    <PurchasingPermissionBoundary permission={P.landedCostRead}>
      <LandedCostEditorPage
        key={params.landedCostId}
        {...(params.landedCostId === undefined ? {} : { landedCostId: params.landedCostId })}
        canUpdate={hasPermission(P.landedCostUpdate)}
        canDelete={hasPermission(P.landedCostDelete)}
        canPost={hasPermission(P.landedCostPost)}
        canReadSuppliers={hasPermission(P.partyRead)}
        canReadVariants={hasPermission(P.variantRead)}
        canReadGrns={hasPermission(P.grnRead)}
        onCreated={nav.openLandedCost}
        onDeleted={nav.openLandedCosts}
      />
    </PurchasingPermissionBoundary>
  );
}

function ReorderSuggestionsRoute() {
  const nav = usePurchasingNav();
  const { hasPermission } = useSession();
  return (
    <PurchasingPermissionBoundary permission={P.reorderSuggestionRead}>
      <ReorderSuggestionsPage
        canConvert={hasPermission(P.reorderSuggestionConvert)}
        canDismiss={hasPermission(P.reorderSuggestionDismiss)}
        canCalculate={hasPermission(P.reorderSuggestionCalculate)}
        canReadSuppliers={hasPermission(P.partyRead)}
        canReadVariants={hasPermission(P.variantRead)}
        canReadWarehouses={hasPermission(P.warehouseRead)}
        onOpenPurchaseOrder={nav.openPurchaseOrder}
      />
    </PurchasingPermissionBoundary>
  );
}

function SupplierItemsRoute() {
  const { hasPermission } = useSession();
  return (
    <PurchasingPermissionBoundary permission={P.supplierItemRead}>
      <SupplierItemsPage
        canCreate={hasPermission(P.supplierItemCreate)}
        canUpdate={hasPermission(P.supplierItemUpdate)}
        // Delete pre-reads the entity ETag, so the affordance needs both grants.
        canDelete={hasPermission(P.supplierItemRead) && hasPermission(P.supplierItemDelete)}
        canReadSuppliers={hasPermission(P.partyRead)}
        canReadVariants={hasPermission(P.variantRead)}
      />
    </PurchasingPermissionBoundary>
  );
}

function PurchasingConfigRoute() {
  const { company } = useSession();
  return (
    <PurchasingPermissionBoundary permission={P.configRead}>
      {/* The tolerance amount is money, and it is in the company's own
          currency — which the session publishes. */}
      <PurchasingConfigPage baseCurrency={company?.base_currency ?? ""} />
    </PurchasingPermissionBoundary>
  );
}

/**
 * Route factory per the feature-module contract. Paths and permissions:
 *   /purchasing/purchase-orders                       purchasing.purchase_order.read
 *   /purchasing/purchase-orders/new                   purchasing.purchase_order.create
 *   /purchasing/purchase-orders/$purchaseOrderId      purchasing.purchase_order.read
 *   /purchasing/purchase-orders/$id/receive           purchasing.grn.post
 *   /purchasing/grns                                  purchasing.grn.read
 *   /purchasing/grns/$grnId                           purchasing.grn.read
 *   /purchasing/landed-costs                          purchasing.landed_cost.read
 *   /purchasing/landed-costs/new                      purchasing.landed_cost.create
 *   /purchasing/landed-costs/$landedCostId            purchasing.landed_cost.read
 *   /purchasing/reorder-suggestions                   purchasing.reorder_suggestion.read
 *   /purchasing/supplier-items                        purchasing.supplier_item.read
 *   /purchasing/settings                              purchasing.config.read
 */
export function purchasingRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/purchase-orders",
      validateSearch: (search: Record<string, unknown>): PoListSearch => ({
        ...pickString(search, "supplier_id"),
        ...pickString(search, "status"),
      }),
      component: PoListRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/purchase-orders/new",
      component: PoNewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/purchase-orders/$purchaseOrderId",
      component: PoEditorRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/purchase-orders/$purchaseOrderId/receive",
      component: PoReceiveRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/grns",
      validateSearch: (search: Record<string, unknown>): GrnListSearch => ({
        ...pickString(search, "purchase_order_id"),
      }),
      component: GrnListRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/grns/$grnId",
      component: GrnDetailRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/landed-costs",
      component: LandedCostListRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/landed-costs/new",
      component: LandedCostNewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/landed-costs/$landedCostId",
      component: LandedCostDetailRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/reorder-suggestions",
      component: ReorderSuggestionsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/supplier-items",
      component: SupplierItemsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/purchasing/settings",
      component: PurchasingConfigRoute,
    }),
  ] as AnyRoute[];
}
